import React from "react";
import { Link } from "react-router-dom";
import { useContactModal } from "../context/ContactModalContext";
import ImageTile from "../components/ImageTile";
import SectionLabel from "../components/SectionLabel";
import { heroImages, aboutImages } from "../data/images";
import { categories } from "../data/products";

const stats = [
  { v: "1000+", l: "Partner Farms" },
  { v: "360°",  l: "Supply Chain Control" },
  { v: "100%",  l: "Traceable Origin" },
  { v: "24/7",  l: "Buyer Support" },
];

const trustedBy = [
  "Retail EU", "Hotel Chains · GCC", "Wholesale · NA",
  "Food Service · APAC", "HoReCa · AU", "Distributors · AF",
];

const solarSteps = [
  { icon: "☀️", label: "Solar Installation", body: "Years of solar water-pump installs built our rural network." },
  { icon: "🏡", label: "Farm-Gate Access",   body: "Trust-based relationships with thousands of partner farmers." },
  { icon: "🧺", label: "Aggregation & Grading", body: "Centralised pack-houses sort, grade and prep for export." },
  { icon: "🚢", label: "Global Export",      body: "FCL, LCL and air-freight to 30+ countries." },
];

export default function HomePage() {
  const { openModal } = useContactModal();
  return (
    <>
      {/* ── HERO ── */}
      <section className="relative w-full min-h-screen overflow-hidden">
        <ImageTile src={heroImages.farmLandscape} alt="Indian farmland" emoji="🌾"
          className="absolute inset-0"
          imgClassName="scale-105 animate-[float-slow_18s_ease-in-out_infinite]"
          overlayClassName="bg-gradient-to-b from-[#0d1f2d]/35 via-[#0d1f2d]/60 to-[#0d1f2d]" />

        <div className="relative z-10 min-h-screen flex items-center pt-28 pb-16">
          <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
            <div className="grid lg:grid-cols-12 gap-10 items-center">

              <div className="lg:col-span-7 fade-up">
                <div className="inline-flex items-center gap-2 glass-pill rounded-full px-4 py-1.5 mb-6">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-300" />
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-stone-600">Exporting to 30+ countries</span>
                </div>

                <h1 className="font-display text-4xl sm:text-5xl lg:text-[5rem] leading-[1.05] font-semibold">
                  <span className="block text-stone-500">Synergizing</span>
                  <span className="block text-stone-500">renewable energy</span>
                  <span className="block text-gradient py-2">&amp; global agriculture.</span>
                </h1>

                <p className="mt-6 text-stone-700 text-lg max-w-xl leading-relaxed">
                  Headquartered in Pune, Myriad Green Energy is a diversified merchant exporter of fresh vegetables, premium fruits and processed agri products. Our roots in solar energy give us an unbreakable bridge between the farm and the global market.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/products"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl text-sm font-medium text-white bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-700 hover:from-emerald-600 hover:to-emerald-700 transition shadow-xl shadow-emerald-600/30">
                    Explore Our Produce
                    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  </Link>
                  <button onClick={openModal}
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl text-sm font-medium text-stone-900 glass-pill hover:bg-stone-900/8 transition">
                    Start an Inquiry
                  </button>
                </div>
              </div>

              <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.2s" }}>
                <div className="relative">
                  <div className="glass-strong shimmer-border rounded-[2rem] p-4 md:p-5">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { src: heroImages.harvestHands, emoji: "🌾", label: "Harvest" },
                        { src: heroImages.spiceMarket,  emoji: "🌶️", label: "Spices" },
                        { src: heroImages.basket,       emoji: "🥭", label: "Fruits" },
                        { src: aboutImages.logistics,   emoji: "🚢", label: "Shipping" },
                      ].map((t, i) => (
                        <ImageTile key={i} src={t.src} alt={t.label} emoji={t.emoji}
                          className="aspect-square rounded-2xl overflow-hidden"
                          imgClassName="hover:scale-110 transition-transform duration-700"
                          overlayClassName="bg-gradient-to-t from-black/70 via-transparent to-transparent">
                          <div className="absolute bottom-2 left-3 text-xs font-medium text-white">{t.label}</div>
                        </ImageTile>
                      ))}
                    </div>
                  </div>
                  <div className="absolute -top-6 -left-6 glass rounded-2xl p-4 shadow-xl hidden md:block animate-float-reverse">
                    <div className="text-[10px] uppercase tracking-widest text-stone-400">Certified</div>
                    <div className="font-display text-sm mt-1 text-stone-900">FSSAI · APEDA</div>
                  </div>
                  <div className="absolute -bottom-5 -right-4 glass rounded-2xl p-4 shadow-xl hidden md:flex items-center gap-3 animate-float-slow">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-200 to-amber-400 flex items-center justify-center text-lg">⭐</div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-stone-400">Rating</div>
                      <div className="text-sm font-medium text-stone-900">Premium Grade A</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="relative mt-10 z-10">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="glass-strong shimmer-border noise rounded-3xl p-6 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s, i) => (
              <div key={s.l} className={`text-center ${i !== 0 ? "md:border-l md:border-stone-900/10" : ""} px-2`}>
                <div className="font-display text-4xl md:text-5xl font-semibold text-gradient">{s.v}</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUSTED BY ── */}
      <section className="relative pt-16 pb-4">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="text-center text-[11px] uppercase tracking-[0.25em] text-stone-400 mb-6">
            Serving buyers across · 30+ Countries
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            {trustedBy.map(t => (
              <div key={t} className="glass-pill rounded-full px-5 py-2.5 text-xs md:text-sm text-stone-500 hover:text-stone-900 transition">{t}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT TEASER ── */}
      <section className="relative py-24 md:py-32">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <SectionLabel number="01">Who We Are</SectionLabel>
              {/* About teaser intentionally precedes the Solar Heritage section */}
              <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight text-stone-500">
                Rooted in heritage,{" "}
                <span className="text-gradient">engineered for the world.</span>
              </h2>
              <p className="mt-6 text-stone-600 leading-relaxed">
                We partner directly with growers across Maharashtra, Karnataka, Kerala and Tamil Nadu — ensuring every consignment is traceable, fairly sourced, and meets the rigorous demands of international buyers.
              </p>
              <Link to="/about" className="mt-7 inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 transition">
                Read our story
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="grid grid-cols-2 gap-3">
                <ImageTile src={aboutImages.farmer} alt="Farmer" emoji="🌾"
                  className="aspect-[3/4] rounded-3xl overflow-hidden"
                  imgClassName="hover:scale-110 transition-transform duration-700" />
                <div className="flex flex-col gap-3">
                  <ImageTile src={aboutImages.fieldWide} alt="Farm field" emoji="🌱"
                    className="aspect-square rounded-3xl overflow-hidden"
                    imgClassName="hover:scale-110 transition-transform duration-700" />
                  <ImageTile src={aboutImages.warehouse} alt="Pack house" emoji="📦"
                    className="aspect-square rounded-3xl overflow-hidden"
                    imgClassName="hover:scale-110 transition-transform duration-700" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SOLAR → FARM HERITAGE ── */}
      <section className="relative py-20 md:py-28">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="max-w-3xl mb-12">
            <SectionLabel number="02">The Myriad Advantage</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight text-stone-500">
              From <span className="text-gradient">solar power</span> to farm power.
            </h2>
            <p className="mt-5 text-stone-600 max-w-2xl leading-relaxed">
              As an established solar installation company, we have spent years building direct, trust-based relationships with thousands of farmers during the installation of solar water pumps. That bond transforms us from a typical trader into a genuine farm-gate partner — with unmatched access, transparency and reliability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {solarSteps.map((s, i) => (
              <div key={s.label}
                className="relative glass rounded-3xl p-6 md:p-7 glass-hover fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-linear-to-br from-emerald-600 to-emerald-700 text-white flex items-center justify-center text-xl shadow-lg shadow-emerald-600/25">
                    {s.icon}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400">Step 0{i + 1}</div>
                </div>
                <h3 className="font-display text-xl font-medium text-stone-900">{s.label}</h3>
                <p className="text-sm text-stone-500 mt-2 leading-relaxed">{s.body}</p>
                {i < solarSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-gradient-to-r from-amber-300/40 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED CATEGORIES ── */}
      <section className="relative py-20 md:py-28">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <SectionLabel number="03">Our Produce</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight text-stone-500">
                A harvest <span className="text-gradient">worth crossing oceans for.</span>
              </h2>
            </div>
            <Link to="/products"
              className="inline-flex items-center gap-2 self-start md:self-auto px-5 py-3 rounded-2xl text-sm glass-pill hover:bg-stone-900/8 transition text-stone-900">
              View all products
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {categories.map((c, i) => (
              <Link key={c.key} to={`/products/${c.key}`}
                className="group glass rounded-3xl overflow-hidden glass-hover fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}>
                <ImageTile src={c.hero} alt={c.label} emoji={c.emoji} className="aspect-[4/5]"
                  imgClassName="group-hover:scale-110"
                  overlayClassName="bg-gradient-to-t from-black/90 via-black/30 to-transparent">
                  <div className="absolute top-3 left-3 glass-pill rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest text-stone-700">{c.short}</div>
                  <div className="absolute bottom-5 left-5 right-5">
                    <div className="font-display text-2xl md:text-3xl font-semibold text-white">{c.label}</div>
                    <div className="mt-2 text-xs text-stone-300">{c.items.length} product{c.items.length > 1 ? "s" : ""}</div>
                    <div className="mt-4 inline-flex items-center gap-2 text-emerald-100 text-sm">
                      Explore
                      <svg viewBox="0 0 24 24" className="w-4 h-4 translate-x-0 group-hover:translate-x-1 transition" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </ImageTile>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative py-20 md:py-28">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-[1600px] mx-auto">
          <div className="relative glass-strong shimmer-border noise rounded-[2.5rem] p-10 md:p-16 overflow-hidden">
            <ImageTile src={aboutImages.shipping} alt="Shipping port" emoji="🚢"
              className="absolute inset-0 opacity-45"
              fallbackGradient="from-indigo-900 via-indigo-700/50 to-slate-900"
              overlayClassName="bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
            <div className="relative max-w-2xl">
              <SectionLabel number="04">Get Started</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight text-stone-500">
                Ready to source <br />
                <span className="text-gradient">direct from origin?</span>
              </h2>
              <p className="mt-5 text-stone-700">
                Share your requirements — volume, destination, target pricing — and our export team will return within one business day with a tailored proposal.
              </p>
              <button onClick={openModal}
                className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-2xl text-sm font-medium text-white bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-700 hover:from-emerald-600 hover:to-emerald-700 transition shadow-xl shadow-emerald-600/30">
                Start an Inquiry
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
